// DS: CR55
/*  The top portion of a single topic page containing a hero image
 *  and associated links.
 */
import React, { useState, useCallback } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiTopic } from '@wix/da-papi-types';
import { useScrollAware } from '@wix/da-hooks/pkg/useScrollAware';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import Flex from '@wix/da-ds/pkg/Flex';
import ArtStageArrowL from '@wix/da-ds/pkg/Icons/ArtStageArrowL';
import shortenNumber from '@wix/da-ds/pkg/utils/shortenNumber';
import RelatedTopics from '../RelatedTopics';
import ArtBackdrop from '../ArtBackdrop';
import TopicActions from './_partials/TopicActions';
import { BackToClickBiEvent, BiData, BiEvent } from '@wix/da-bi/pkg/events';

import s from './TopicHeader.scss';

const TOPIC_HEADER_HEIGHT = 336;

export interface Props {
  /** the topic being explored */
  topic: PapiTopic;
  /** an array of topics related to the current one */
  relatedTopics?: PapiTopic[];
  /** optional count of deviations that fall into this topic */
  estTotal?: number;
}

export const TopicHeader: React.FC<Props> = props => {
  const { topic, relatedTopics, estTotal } = props;
  const { name, description, typeId, itemId, exampleDeviations } = topic;
  const { t } = useTranslation();

  const pastHeader = useIsPastHeader();
  const backgroundImage = exampleDeviations?.[0];

  const topicBiData: BiEvent = BiData<any>({
    typeid: typeId,
    itemid: itemId,
  }) as any;
  if (!name || !description) {
    return null;
  }

  return (
    <>
      <header className={s['header']}>
        {backgroundImage ? (
          <ArtBackdrop
            artClassName={s['background-art']}
            attributionClassName={s['background-attribution']}
            deviation={backgroundImage}
          />
        ) : (
          <div className={s['missing-bg']} />
        )}
        <BackToAllTopics extraBiData={topicBiData} />

        <Flex className={s['topic-main']} justifyContent="space-between">
          <div className={s['topic-info']}>
            <h1 className={s['title']}>{name}</h1>
            <p className={s['description']}>
              {description}
              {estTotal && (
                <span className={s['results']}>
                  {' / '}
                  <em>{shortenNumber(estTotal)}</em>
                  {t('topicPage.topicHeader.results', {
                    count: estTotal,
                  })}
                </span>
              )}
            </p>

            <RelatedTopics
              className={s['related-topics']}
              topics={relatedTopics}
            />
          </div>
          <TopicActions topic={topic} />
        </Flex>
      </header>

      <Section
        className={classnames(s['sticky'], pastHeader && s['shown'])}
        // Screen readers already have access to this content
        aria-hidden={true}
      >
        <BackToAllTopics extraBiData={topicBiData} />
        <Heading className={s['title']}>{name}</Heading>
      </Section>
    </>
  );
};
TopicHeader.displayName = 'TopicHeaderCR55';
export default TopicHeader;

/** Link back to the list page */
const BackToAllTopics: React.FC<{ extraBiData?: BiEvent }> = ({
  extraBiData = {},
}) => {
  const { t } = useTranslation();
  const biData = BiData<BackToClickBiEvent>({
    evid: 304,
    ...extraBiData,
  } as any);

  return (
    <BiLink className={s['back-to-topics']} href="/topic" biData={biData}>
      <ArtStageArrowL customSizable className={s['back-to-topics-arrow']} />
      {t('topicListPage.allTopics')}
    </BiLink>
  );
};
BackToAllTopics.displayName = 'BackToAllTopics';

function useIsPastHeader(): boolean {
  const [pastHeader, setPastHeader] = useState(false);

  const scrollHandler = useCallback(
    ({ scrollTop }) => {
      const topOfDeviations = TOPIC_HEADER_HEIGHT - 30;
      setPastHeader(scrollTop >= topOfDeviations);
    },
    [setPastHeader]
  );
  useScrollAware(scrollHandler);

  return pastHeader;
}
